import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaBuilding,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
} from "react-icons/fa";
import AddBranchModal from "../../Components/AddBranchModal";
import axiosInstance from "../../utils/axiosInstance";

const BranchManagement = () => {
  const [branches, setBranches] = useState([]);
  const [hospitalInfo, setHospitalInfo] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
  const hospitalId = userDetails.hospitalId;

  useEffect(() => {
    if (hospitalId) {
      fetchHospitalInfo();
      fetchBranches();
    }
  }, [hospitalId]);

  const fetchHospitalInfo = async () => {
    try {
      const response = await axiosInstance.get(`/hospitals/${hospitalId}`);
      setHospitalInfo(response.data);
    } catch (err) {
      console.error("Error fetching hospital info:", err);
    }
  };

  const fetchBranches = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/hospitals/branches");
      // Handle both old format (direct array) and new format (with success wrapper)
      const branchesData = response.data.branches || response.data;
      setBranches(branchesData);
      setError(null);
    } catch (err) {
      setError("Error fetching branches: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBranch = () => {
    setEditingBranch(null);
    setIsModalOpen(true);
  };

  const handleEditBranch = (branch) => {
    setEditingBranch(branch);
    setIsModalOpen(true);
  };

  const handleDeleteBranch = async (branchId) => {
    if (!window.confirm("Are you sure you want to delete this branch?")) {
      return;
    }

    try {
      await axiosInstance.delete(`/hospitals/branches/${branchId}`);
      fetchBranches();
      alert("Branch deleted successfully");
    } catch (err) {
      alert("Error deleting branch: " + err.message);
    }
  };

  const handleModalClose = (shouldRefresh) => {
    setIsModalOpen(false);
    setEditingBranch(null);
    if (shouldRefresh) {
      fetchBranches();
    }
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">
          Branch Management
        </h1>

        {/* Hospital Information Card */}
        {hospitalInfo && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-md p-6 mb-6 border border-blue-200">
            <div className="flex items-center mb-4">
              <FaBuilding className="text-blue-600 text-2xl mr-3" />
              <h2 className="text-2xl font-bold text-gray-800">
                {hospitalInfo.name}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start">
                <FaMapMarkerAlt className="text-gray-500 mt-1 mr-2" />
                <div>
                  <p className="text-sm text-gray-600 font-semibold">Address</p>
                  <p className="text-gray-800">
                    {hospitalInfo.address}
                    {hospitalInfo.city && `, ${hospitalInfo.city}`}
                    {hospitalInfo.state && `, ${hospitalInfo.state}`}
                    {hospitalInfo.pincode && ` - ${hospitalInfo.pincode}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <FaPhone className="text-gray-500 mr-2" />
                <div>
                  <p className="text-sm text-gray-600 font-semibold">Phone</p>
                  <p className="text-gray-800">
                    {hospitalInfo.phoneNumber || "N/A"}
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <FaEnvelope className="text-gray-500 mr-2" />
                <div>
                  <p className="text-sm text-gray-600 font-semibold">Email</p>
                  <p className="text-gray-800">{hospitalInfo.email || "N/A"}</p>
                </div>
              </div>
              {hospitalInfo.website && (
                <div className="flex items-center">
                  <div>
                    <p className="text-sm text-gray-600 font-semibold">
                      Website
                    </p>
                    <a
                      href={hospitalInfo.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {hospitalInfo.website}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Add Branch Button */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-700">Branches</h2>
          <button
            onClick={handleAddBranch}
            className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FaPlus className="mr-2" />
            Add Branch
          </button>
        </div>

        {/* Branches Table */}
        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading branches...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        ) : branches.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
            <FaBuilding className="mx-auto text-gray-400 text-5xl mb-4" />
            <p className="text-gray-600 text-lg mb-2">No branches found</p>
            <p className="text-gray-500 mb-4">
              Get started by adding your first branch
            </p>
            <button
              onClick={handleAddBranch}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add First Branch
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Branch Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {branches.map((branch) => (
                  <tr key={branch.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FaBuilding className="text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900">
                          {branch.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-700">
                        {branch.address || "N/A"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-700">
                        {branch.phoneNumber || "N/A"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-700">
                        {branch.email || "N/A"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditBranch(branch)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                        title="Edit"
                      >
                        <FaEdit className="inline text-lg" />
                      </button>
                      <button
                        onClick={() => handleDeleteBranch(branch.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <FaTrash className="inline text-lg" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Branch Modal */}
      {isModalOpen && (
        <AddBranchModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          branch={editingBranch}
          hospitalId={hospitalId}
        />
      )}
    </div>
  );
};

export default BranchManagement;
